import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, useTheme } from '@mui/material';
import { useAppState } from 'src/state-management/state-provider';
import { navConfig } from './nav.config';
import Header from './Header';
import Sidebar from './Sidebar';
import { getNavItemsForRoles } from './nav.util';
import DynamicBreadcrumbs from 'src/view/components/navigation/DynamicBreadcrumbs';
import { INTERACTIVE_MAP_ROUTES } from 'src/constants';
import { UserRole } from 'src/api/Api';
import Footer from './Footer';

interface NavigationProps {
  children: React.ReactNode;
}

const drawerWidth = 240; // Should match the width defined in Sidebar.tsx
const headerHeight = 73; // Height of the header

const Navigation: React.FC<NavigationProps> = ({ children }) => {
  const theme = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    const storedState = localStorage.getItem('sidebarOpen');
    return storedState ? JSON.parse(storedState) : false;
  });

  const { state } = useAppState();
  const location = useLocation();
  const isInteractiveMap = INTERACTIVE_MAP_ROUTES.some((route) =>
    location.pathname.includes(route)
  );

  useEffect(() => {
    localStorage.setItem('sidebarOpen', JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const userRoles = state.user?.role;
  const navItems = getNavItemsForRoles(userRoles, navConfig);
  const isAdmin = userRoles?.includes(UserRole.Admin);
  const showSidebar = !isAdmin && location.pathname !== '/help-support';

  // This calculation should be kept in sync with Sidebar.tsx
  const collapsedDrawerWidth = `calc(${theme.spacing(7)} + 1px)`;

  return (
    <Box sx={{ display: 'flex', backgroundColor: '#0A0A0A', minHeight: '100vh' }}>
      <Header />
      {showSidebar && (
        <Sidebar
          open={sidebarOpen}
          onToggle={handleSidebarToggle}
          navItems={navItems}
        />
      )}
      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          p: isInteractiveMap ? 0 : (isAdmin ? 0 : 3), // No double padding for admin
          marginTop: `${headerHeight}px`,
          height: isInteractiveMap
            ? `calc(100vh - ${headerHeight}px)`
            : 'auto',
          marginLeft: showSidebar ? (sidebarOpen ? `${drawerWidth}px` : collapsedDrawerWidth) : 0,
          transition: theme.transitions.create('margin-left', {
            easing: theme.transitions.easing.sharp,
            duration: sidebarOpen
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
          backgroundColor: '#0A0A0A',
        }}
      >
        {!isInteractiveMap && (
          <Box sx={{ px: isAdmin ? 4 : 0, pt: isAdmin ? 2 : 0 }}>
            <DynamicBreadcrumbs />
          </Box>
        )}
        {children}
        {!isInteractiveMap && <Footer />}
      </Box>
    </Box>
  );
};

export default Navigation;
